import React from "react";

interface ResponseTimeBadgeProps {
  createdAt: string;
  respondedAt: string | null | undefined;
  thresholds?: { greenSecs: number; amberSecs: number };
  size?: "compact" | "full";
  label?: string;
}

function formatDuration(seconds: number): string {
  if (seconds < 60) return `${seconds}s`;
  if (seconds < 3600) {
    const m = Math.floor(seconds / 60);
    return `${m}m`;
  }
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
}

export function ResponseTimeBadge({
  createdAt,
  respondedAt,
  thresholds = { greenSecs: 3600, amberSecs: 14400 },
  size = "compact",
  label,
}: ResponseTimeBadgeProps) {
  const created = new Date(createdAt).getTime();

  if (!respondedAt) {
    const elapsed = Math.floor((Date.now() - created) / 1000);
    const color =
      elapsed <= thresholds.greenSecs
        ? "#22c55e"
        : elapsed <= thresholds.amberSecs
        ? "#f59e0b"
        : "#ef4444";

    if (size === "compact") {
      return (
        <span
          data-testid="badge-response-time-pending"
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 3,
            padding: "2px 7px",
            borderRadius: 4,
            background: `${color}18`,
            color,
            border: `1px solid ${color}33`,
            fontSize: 9,
            fontWeight: 700,
            letterSpacing: "0.07em",
            fontFamily: "monospace",
            textTransform: "uppercase" as const,
            whiteSpace: "nowrap" as const,
          }}
        >
          <span style={{ opacity: 0.7 }}>⏱</span>{formatDuration(elapsed)} elapsed
        </span>
      );
    }

    return (
      <div
        data-testid="badge-response-time-pending"
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 6,
          padding: "5px 10px",
          borderRadius: 6,
          background: `${color}14`,
          border: `1px solid ${color}33`,
          color,
        }}
      >
        <span style={{ fontSize: 14 }}>⏱</span>
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, fontFamily: "monospace" }}>
            {label ?? "No response yet"} — {formatDuration(elapsed)} elapsed
          </div>
          <div style={{ fontSize: 9, opacity: 0.6, fontFamily: "monospace" }}>
            waiting since booking submitted
          </div>
        </div>
      </div>
    );
  }

  const elapsed = Math.floor((new Date(respondedAt).getTime() - created) / 1000);
  const color =
    elapsed <= thresholds.greenSecs
      ? "#22c55e"
      : elapsed <= thresholds.amberSecs
      ? "#f59e0b"
      : "#ef4444";

  if (size === "compact") {
    return (
      <span
        data-testid="badge-response-time-done"
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 3,
          padding: "2px 7px",
          borderRadius: 4,
          background: `${color}18`,
          color,
          border: `1px solid ${color}33`,
          fontSize: 9,
          fontWeight: 700,
          letterSpacing: "0.07em",
          fontFamily: "monospace",
          textTransform: "uppercase" as const,
          whiteSpace: "nowrap" as const,
        }}
      >
        <span style={{ opacity: 0.7 }}>✓</span>{formatDuration(elapsed)} response
      </span>
    );
  }

  return (
    <div
      data-testid="badge-response-time-done"
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        padding: "5px 10px",
        borderRadius: 6,
        background: `${color}14`,
        border: `1px solid ${color}33`,
        color,
      }}
    >
      <span style={{ fontSize: 14 }}>✓</span>
      <div>
        <div style={{ fontSize: 11, fontWeight: 700, fontFamily: "monospace" }}>
          {label ?? "Response time"}: {formatDuration(elapsed)}
        </div>
        <div style={{ fontSize: 9, opacity: 0.6, fontFamily: "monospace" }}>
          {elapsed <= thresholds.greenSecs ? "within target" : elapsed <= thresholds.amberSecs ? "slightly delayed" : "past target"}
        </div>
      </div>
    </div>
  );
}
